import { useEffect, useState } from "react";
import axios from "axios";
import { STRATEGIES } from "@/data/strategies";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Target,
  ShieldAlert,
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
  XCircle,
  CheckCircle,
  LogIn,
  LogOut as LogOutIcon,
  Star,
  Zap,
  Loader2,
  RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ icon: Icon, title, color = "pink", children }) {
  return (
    <Card className="bg-white border-2 border-pink-200 rounded-2xl overflow-hidden">
      <div className={`h-1 bg-${color}-400`} />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Icon className={`w-5 h-5 text-${color}-500`} />
          <h3 className="font-bold text-sm sm:text-base text-zinc-800">{title}</h3>
        </div>
        <div className="text-xs sm:text-sm text-zinc-700 leading-relaxed">{children}</div>
      </CardContent>
    </Card>
  );
}

function StarRow({ count }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3 h-3 ${i <= count ? "text-yellow-400 fill-yellow-400" : "text-zinc-300"}`} />
      ))}
    </div>
  );
}

function SignalCard({ strategy, signal, loading, error, lang, onRefresh, symbol, timeframe, hidden = false }) {
  const isAr = lang === "ar";
  if (hidden) {
    return (
      <Card className="bg-pink-50 border-2 border-pink-200 rounded-2xl" data-testid="education-only-notice">
        <CardContent className="p-4 text-xs sm:text-sm text-pink-800">
          {isAr
            ? "أنت في الخطة التعليمية (Education). للحصول على الإشارات المباشرة، قم بالترقية إلى Basic أو Advance."
            : "You are on the Education plan. Upgrade to Basic or Advance to unlock live signals."}
        </CardContent>
      </Card>
    );
  }
  if (strategy.id === "war-crisis") {
    return (
      <Card className="bg-amber-50 border-2 border-amber-300 rounded-2xl">
        <CardContent className="p-4 text-xs sm:text-sm text-amber-800">
          {isAr
            ? "هذه الاستراتيجية تعليمية فقط — لا تُنتج إشارات آلية. اعتمد على التحليل البشري للأحداث الجيوسياسية."
            : "Educational strategy only — no automated signals. Use human judgment for geopolitical events."}
        </CardContent>
      </Card>
    );
  }
  const sigType = signal?.signal || "NEUTRAL";
  const sigColor = sigType === "BUY" ? "bg-emerald-500" : sigType === "SELL" ? "bg-red-500" : "bg-zinc-400";
  const SigIcon = sigType === "BUY" ? TrendingUp : sigType === "SELL" ? TrendingDown : Minus;
  return (
    <Card className="bg-white border-4 border-pink-300 rounded-3xl overflow-hidden shadow-lg shadow-pink-200/40" data-testid="strategy-signal-card">
      <div className="h-2 bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400" />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-pink-500" />
            <h3 className="font-bold text-sm sm:text-base text-zinc-800">
              {isAr ? "الإشارة المباشرة" : "Live Signal"}
            </h3>
            <Badge variant="outline" className="text-[10px] border-pink-300 text-pink-600">
              {symbol} · {timeframe}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRefresh}
            disabled={loading}
            className="text-pink-500 h-8 w-8"
            data-testid="signal-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        {loading && !signal && (
          <div className="flex items-center justify-center py-8 text-pink-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className="text-sm">{isAr ? "جاري التحليل..." : "Analyzing..."}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs">
            {error}
          </div>
        )}

        {signal && (
          <>
            <div className="flex items-center justify-between mb-4">
              <Badge className={`${sigColor} text-white text-base sm:text-lg px-4 py-2 rounded-full font-bold`} data-testid="signal-badge">
                <SigIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5" />
                {sigType}
              </Badge>
              <div className="text-right">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide">{isAr ? "الثقة" : "Confidence"}</p>
                <p className="text-xl sm:text-2xl font-bold text-pink-600">{signal.confidence}%</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3">
              <div className="bg-pink-50 rounded-xl p-2 text-center">
                <p className="text-[10px] text-zinc-500">{isAr ? "السعر الحالي" : "Current"}</p>
                <p className="text-xs sm:text-sm font-mono font-bold text-zinc-800">{signal.current_price}</p>
              </div>
              <div className="bg-zinc-50 rounded-xl p-2 text-center">
                <p className="text-[10px] text-zinc-500">ATR</p>
                <p className="text-xs sm:text-sm font-mono font-bold text-zinc-800">{signal.atr}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-2 text-center">
                <p className="text-[10px] text-zinc-500">{isAr ? "الإشارة" : "Signal"}</p>
                <p className="text-xs sm:text-sm font-mono font-bold text-blue-700">{signal.signal}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-2 text-center">
                <p className="text-[10px] text-zinc-500">{isAr ? "الثقة" : "Confidence"}</p>
                <p className="text-xs sm:text-sm font-mono font-bold text-purple-700">{signal.confidence}%</p>
              </div>
            </div>

            {/* Dual ATR Entries */}
            {(signal.entry_aggressive || signal.entry_conservative) && (
              <div className="space-y-2 mb-3">
                {signal.entry_aggressive && (
                  <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-2.5" data-testid="strategy-entry-aggressive">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-amber-700">{isAr ? "دخول جريء (0.3×ATR)" : "Aggressive (0.3×ATR)"}</span>
                      <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700">{isAr ? "مبكر" : "Early"}</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 text-[11px]">
                      <div><span className="text-zinc-500 block">Entry</span><span className="font-mono font-bold text-zinc-800">{signal.entry_aggressive.price}</span></div>
                      <div><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-600">{signal.entry_aggressive.stop_loss}</span></div>
                      <div><span className="text-zinc-500 block">TP1</span><span className="font-mono text-emerald-600">{signal.entry_aggressive.take_profit_1}</span></div>
                      <div><span className="text-zinc-500 block">TP2</span><span className="font-mono text-emerald-600">{signal.entry_aggressive.take_profit_2}</span></div>
                    </div>
                  </div>
                )}
                {signal.entry_conservative && (
                  <div className="border-2 border-cyan-300 bg-cyan-50 rounded-xl p-2.5" data-testid="strategy-entry-conservative">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-cyan-700">{isAr ? "دخول متحفظ (0.7×ATR)" : "Conservative (0.7×ATR)"}</span>
                      <Badge variant="outline" className="text-[9px] border-cyan-400 text-cyan-700">{isAr ? "آمن" : "Safer"}</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 text-[11px]">
                      <div><span className="text-zinc-500 block">Entry</span><span className="font-mono font-bold text-zinc-800">{signal.entry_conservative.price}</span></div>
                      <div><span className="text-zinc-500 block">SL</span><span className="font-mono text-red-600">{signal.entry_conservative.stop_loss}</span></div>
                      <div><span className="text-zinc-500 block">TP1</span><span className="font-mono text-emerald-600">{signal.entry_conservative.take_profit_1}</span></div>
                      <div><span className="text-zinc-500 block">TP2</span><span className="font-mono text-emerald-600">{signal.entry_conservative.take_profit_2}</span></div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {signal.notes && signal.notes.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-2.5 text-xs text-purple-800">
                <strong>{isAr ? "ملاحظة:" : "Note:"}</strong> {signal.notes[0]}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function StrategyDetailPage({ strategyId, lang, onBack, symbol = "EUR/USD", timeframe = "1h", educationOnly = false }) {
  const strategy = STRATEGIES.find((s) => s.id === strategyId);
  const isAr = lang === "ar";
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSignal = async () => {
    if (!strategy || strategy.id === "war-crisis") return;
    if (educationOnly) return;
    setLoading(true);
    setError("");
    try {
      const urlSymbol = symbol.replace("/", "-");
      const res = await axios.get(`${API}/strategy-signal/${strategy.id}/${urlSymbol}`, {
        params: { timeframe },
      });
      setSignal(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || (isAr ? "تعذّر جلب الإشارة" : "Failed to fetch signal"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId, symbol, timeframe, educationOnly]);

  if (!strategy) return null;
  const Icon = strategy.icon;
  const t = (obj) => obj?.[lang] || obj?.en;

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="space-y-4">
      {/* Back button */}
      <Button
        variant="outline"
        onClick={onBack}
        className="gap-2 border-pink-300 text-pink-600 bg-white hover:bg-pink-50"
        data-testid="strategy-back-btn"
      >
        {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
        {isAr ? "العودة للقائمة" : "Back to list"}
      </Button>

      {/* Hero */}
      <Card className="bg-white border-2 border-pink-200 rounded-3xl overflow-hidden">
        <div className={`h-2 bg-gradient-to-r ${strategy.color}`} />
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-gradient-to-br ${strategy.color} flex items-center justify-center shadow-lg`}>
              <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold text-pink-600 leading-tight">
                {t(strategy.name)}
              </h1>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                {t(strategy.summary)}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Badge className="bg-red-100 text-red-700 border border-red-200 text-xs">
                  <ShieldAlert className="w-3 h-3 mr-1" />
                  {isAr ? "المخاطرة:" : "Risk:"} {t(strategy.risk)}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Signal */}
      <SignalCard
        strategy={strategy}
        signal={signal}
        loading={loading}
        error={error}
        lang={lang}
        onRefresh={fetchSignal}
        symbol={symbol}
        timeframe={timeframe}
        hidden={educationOnly}
      />

      {/* When to use */}
      <Section icon={Clock} title={isAr ? "متى تستخدم" : "When to use"} color="blue">
        <ul className="space-y-1.5 list-disc list-inside">
          {t(strategy.whenToUse).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </Section>

      {/* Markets */}
      <Section icon={Globe} title={isAr ? "الأسواق المناسبة" : "Suitable markets"} color="cyan">
        <ul className="space-y-1.5 list-disc list-inside">
          {t(strategy.markets).map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      </Section>

      {/* Entry rules */}
      <Section icon={LogIn} title={isAr ? "قواعد الدخول" : "Entry rules"} color="green">
        <ul className="space-y-1.5 list-disc list-inside">
          {t(strategy.entry).map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      </Section>

      {/* Exit / SL */}
      <Section icon={LogOutIcon} title={isAr ? "قواعد الخروج / وقف الخسارة" : "Exit & stop-loss rules"} color="red">
        <ul className="space-y-1.5 list-disc list-inside">
          {t(strategy.exit).map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      </Section>

      {/* Timeframes table */}
      <Card className="bg-white border-2 border-pink-200 rounded-2xl overflow-hidden">
        <div className="h-1 bg-purple-400" />
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-purple-500" />
            <h3 className="font-bold text-sm sm:text-base text-zinc-800">
              {isAr ? "الأطر الزمنية والثقة" : "Timeframes & Confidence"}
            </h3>
          </div>
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-pink-100">
                  <th className={`py-2 px-2 ${isAr ? "text-right" : "text-left"} text-zinc-500 font-semibold`}>{isAr ? "الإطار" : "Timeframe"}</th>
                  <th className={`py-2 px-2 ${isAr ? "text-right" : "text-left"} text-zinc-500 font-semibold`}>{isAr ? "الثقة" : "Confidence"}</th>
                  <th className={`py-2 px-2 ${isAr ? "text-right" : "text-left"} text-zinc-500 font-semibold`}>{isAr ? "التقييم" : "Rating"}</th>
                  <th className={`py-2 px-2 ${isAr ? "text-right" : "text-left"} text-zinc-500 font-semibold`}>{isAr ? "R/R" : "R/R"}</th>
                </tr>
              </thead>
              <tbody>
                {strategy.timeframes.map((row, i) => (
                  <tr key={i} className="border-b border-pink-50 last:border-0">
                    <td className="py-2 px-2 font-semibold text-zinc-800">{row.tf}</td>
                    <td className="py-2 px-2 text-emerald-600 font-bold">{row.confidence}</td>
                    <td className="py-2 px-2"><StarRow count={row.stars} /></td>
                    <td className="py-2 px-2 text-blue-600">{row.rr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pros & Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        <Section icon={CheckCircle} title={isAr ? "المميزات" : "Pros"} color="green">
          <ul className="space-y-1.5 list-disc list-inside">
            {t(strategy.pros).map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </Section>
        <Section icon={XCircle} title={isAr ? "العيوب" : "Cons"} color="red">
          <ul className="space-y-1.5 list-disc list-inside">
            {t(strategy.cons).map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </Section>
      </div>

      {/* Footer disclaimer */}
      <div className="text-center text-xs text-zinc-500 py-3">
        <TrendingUp className="w-4 h-4 inline mr-1 text-pink-400" />
        {isAr ? "هذه استراتيجية تعليمية — ادمج دائماً مع إدارة المخاطر" : "Educational strategy — always combine with risk management"}
      </div>
    </div>
  );
}
